import { Component, Input, OnInit, Output, EventEmitter, inject } from '@angular/core';
import { TitleCasePipe } from '@angular/common';
import { ClientChoreStatus } from 'app/core/enums/client-chore-status.enum';
import { AvailableClientChore } from 'app/core/models/available-client-chore.model';
import { Client } from 'app/core/models/client.model';
import { MinimalClient } from 'app/core/models/minimal-client.model';
import { ClientChoreFilters } from 'app/core/models/user-settings/client-chore-settings.model';
import { User } from 'app/core/models/user.model';
import { UserType } from 'app/core/enums/user-type.enum';
import { ClientChoresService } from 'app/core/services/client-chores.service';
import { sortBy } from 'lodash-es';
import { NgSelectComponent } from '@ng-select/ng-select';
import { ClientSelectComponent } from '../../../shared/components/client-select/client-select.component';
import { FormsModule } from '@angular/forms';
import { MatSlideToggle } from '@angular/material/slide-toggle';

class ClientChoreStatusOption {
  value: string;
  display: string
}

class ClientUserSubmittedByOption {
  fullName: string;
  id: string;
}

@Component({
    selector: 'app-client-chore-filters',
    templateUrl: './client-chore-filters.component.html',
    styleUrls: ['./client-chore-filters.component.scss'],
    imports: [NgSelectComponent, ClientSelectComponent, FormsModule, MatSlideToggle]
})
export class ClientChoreFiltersComponent implements OnInit {
  private clientChoresService = inject(ClientChoresService);

  @Input() filters: ClientChoreFilters = new ClientChoreFilters();
  @Output() filterChange = new EventEmitter<ClientChoreFilters>();

  @Input() clients: (Client | MinimalClient)[];
  @Input() statuses: ClientChoreStatus[];
  @Input() showSubmittedByFilter = true;
  @Input() showNameFilter = true;
  @Input() excludedNames: string[] = [];
  @Input() showInactiveFilter = true;
  @Input() showRequiresReviewFilter = true;

  submittedByOptions: (User | ClientUserSubmittedByOption)[] = [];
  submittedBySelectedIds: (number | string)[] = [];
  clientUserIds: number[] = [];
  availableChores: AvailableClientChore[];
  statusOptions: ClientChoreStatusOption[];

  ngOnInit() {
    this.loadUsers();
    if (this.showNameFilter) this.loadAvailableChores();
    this.mapStatusOptions();
  }

  private loadUsers() {
    this.clientChoresService.getSubmittingUsers().subscribe(result => {
      this.partitionUsers(result)
      this.submittedByOptions = sortBy(this.submittedByOptions, user => user.fullName.toLowerCase());
    })
  }

  private partitionUsers(users: User[]) {
    users.forEach(user => {
      if (user.userType === UserType.captureAdmin) {
        this.submittedByOptions.push(user);
      } else {
        this.clientUserIds.push(user.id);
      }
    });
    if (this.clientUserIds.length > 0) {
      this.submittedByOptions.push(
        { fullName: "Client User", id: 'client_user' }
      )
    }
  }

  private mapStatusOptions() {
    const titleCasePipe = new TitleCasePipe();
    this.statusOptions =
      this.statuses.map(v => ({ value: v, display: titleCasePipe.transform(v) }));
  }

  private loadAvailableChores() {
    this.clientChoresService.getAvailableChores().subscribe(result => {
      const filterableChores =
        result.availableClientChores.filter(availableChore => !this.excludedNames.includes(availableChore.name));

      this.availableChores = sortBy(filterableChores, availableChore => availableChore.displayName);
    });
  }

  onSubmittedByFilterChange() {
    const clientUserSelected = this.submittedBySelectedIds.includes('client_user');

    this.filters.selectedUserIds = clientUserSelected
      ? [
        // Replace "Client User" with actual user IDs for filtering
        ...this.submittedBySelectedIds.filter(id => id !== 'client_user'),
        ...this.clientUserIds
      ]
      : this.submittedBySelectedIds;

    this.filterChange.emit(this.filters);
  }

  onFilterChange() {
    this.filterChange.emit(this.filters);
  }
}
